import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  Grid,
  Alert,
  IconButton,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Chip,
} from '@mui/material';
import { CloudUpload as UploadIcon, Delete as DeleteIcon, WorkOutline, EventNote } from '@mui/icons-material';
import { COLORS } from '../../config/colors';
import { jobDescriptionService } from '../../services/jobDescriptionService';

interface JobDescription {
  id: string;
  jobId?: string;
  title: string;
  jobTitle?: string;
  status: string;
  event?: boolean;
}

interface UploadResumeDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (jobId: string, files: File[], additionalData?: any) => void;
  jobs: JobDescription[];
  loading: boolean;
}

const UploadResumeDialog: React.FC<UploadResumeDialogProps> = ({
  open,
  onClose,
  onUpload,
  jobs,
  loading,
}) => {
  const [uploadMode, setUploadMode] = useState<'jd' | 'event'>('jd');
  const [selectedTargetId, setSelectedTargetId] = useState('');
  const [selectedJobId, setSelectedJobId] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [jobSummary, setJobSummary] = useState('');

  // Event jobs state
  const [eventJobs, setEventJobs] = useState<JobDescription[]>([]);
  const [eventJobsLoading, setEventJobsLoading] = useState(false);
  const [eventJobsError, setEventJobsError] = useState<string | null>(null);

  const [offering, setOffering] = useState('');
  const [level, setLevel] = useState('');
  const [skills, setSkills] = useState('');

  const [dragOver, setDragOver] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) {
      setUploadMode('jd');
      setSelectedTargetId('');
      setSelectedJobId('');
      setFiles([]);
      setJobSummary('');
      setDragOver(false);
      setTouched(false);
    }
  }, [open]);

  useEffect(() => {
    setSelectedTargetId('');
    setSelectedJobId(''); // Also reset selectedJobId
    setTouched(false);
    
    // Fetch event jobs when switching to event mode
    if (uploadMode === 'event') {
      fetchEventJobs();
    }
  }, [uploadMode]);

  // Function to fetch event jobs
  const fetchEventJobs = async () => {
    setEventJobsLoading(true);
    setEventJobsError(null);
    
    try {
      const result = await jobDescriptionService.getAll({
        type: 'event',
        page: 1,
        size: 100, // Fetch more events if needed
      });
      
      // Transform the data to match JobDescription interface
      const transformedJobs: JobDescription[] = result.jobs.map((job: any) => ({
        id: job.jobId || job.id,
        jobId: job.jobId,
        title: job.jobTitle || job.title,
        jobTitle: job.jobTitle,
        status: job.jobStatus || job.status,
        event: true,
      }));
      
      setEventJobs(transformedJobs);
    } catch (error: any) {
      console.error('Error fetching event jobs:', error);
      setEventJobsError(error.message || 'Failed to fetch event jobs');
      setEventJobs([]);
    } finally {
      setEventJobsLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && files.length > 0 && selectedJobId) {
      const timer = setTimeout(() => {
        if (!open) {
             setUploadMode('jd');
          setSelectedTargetId('');
          setSelectedJobId('');
          setFiles([]);
          setJobSummary('');
          setDragOver(false);
          setTouched(false);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [loading, files.length, selectedJobId, open]);


    const filteredTargets = uploadMode === 'event' 
    ? eventJobs // Use fetched event jobs
    : jobs.filter(job => job.event !== true); // Show only JDs (non-events)

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      validateAndSetFiles(newFiles);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);

    if (event.dataTransfer.files) {
      const newFiles = Array.from(event.dataTransfer.files);
      validateAndSetFiles(newFiles);
    }
  };

  const validateAndSetFiles = (newFiles: File[]) => {
    const validFiles = newFiles.filter(file => {
      const validTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (!validTypes.includes(file.type)) {
        console.warn(`File ${file.name} has invalid type: ${file.type}`);
        return false;
      }

      if (file.size > maxSize) {
        console.warn(`File ${file.name} is too large: ${file.size} bytes`);
        return false;
      }

      return true;
    });

    setFiles(prevFiles => [...prevFiles, ...validFiles]);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleSubmit = () => {
    setTouched(true);
    if (selectedTargetId && files.length > 0) {
      const additionalData = uploadMode === 'jd' 
        ? { 
            jobSummary, 
            uploadMode: 'jd' 
          }
        : { 
            offering, 
            level, 
            skills, 
            isEvent: true, 
            uploadMode: 'event' 
          };
      
      onUpload(selectedTargetId, files, additionalData);
    }
  };

  const handleClose = () => {
      setUploadMode('jd');
    setSelectedTargetId('');
    setSelectedJobId('');
    setFiles([]);
    setJobSummary('');
    setDragOver(false);
    setTouched(false);
    onClose();
  };

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const getTotalSize = () => {
    return files.reduce((total, file) => total + file.size, 0);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const showError = touched && !selectedJobId;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <UploadIcon />
          Upload Resumes
        </Box>

        {/* ✅ Upload Mode Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ToggleButtonGroup
            value={uploadMode}
            exclusive
            onChange={(_, value) => {
              if (value) {
                setUploadMode(value);
              }
            }}
            sx={{
              gap: 2,
              '& .MuiToggleButton-root': {
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'grey.300',
                backgroundColor: 'transparent',
                color: 'text.primary',
                transition: 'all 0.2s ease-in-out',
                
                '&.Mui-selected': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  backgroundColor: 'transparent',
                  transform: 'scale(1.02)',
                },
                
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'transparent',
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.15)',
                },
                
                '&.Mui-selected:hover': {
                  backgroundColor: 'transparent',
                  color: 'primary.main',
                  transform: 'scale(1.08)',
                  boxShadow: '0 6px 16px rgba(33, 150, 243, 0.2)',
                },
              },
            }}
          >
            <ToggleButton value="jd">
              <WorkOutline sx={{ mr: 1 }} />
              Job Description
            </ToggleButton>
            <ToggleButton value="event">
              <EventNote sx={{ mr: 1 }} />
              Event-Based
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
          {uploadMode === 'event' 
            ? 'Upload resumes for event-based hiring opportunities'
            : 'Upload resumes for specific job descriptions'
          }
        </Typography>

        <Divider />
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Resume files will be processed and candidate entries will be
            created based on the filename. Ensure filenames follow the pattern:
            "FirstName_LastName.pdf" for best results.
          </Typography>
        </Alert>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* ✅ Dynamic Target Selection Dropdown */}
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label={`Select ${uploadMode === 'event' ? 'Event' : 'Job Description'} *`}
              value={selectedTargetId}
              onChange={e => {
                setSelectedTargetId(e.target.value);
                setSelectedJobId(e.target.value); // Also update selectedJobId for validation
              }}
              onBlur={() => setTouched(true)}
              required
              error={showError}
              disabled={uploadMode === 'event' && eventJobsLoading}
              helperText={
                uploadMode === 'event' && eventJobsLoading
                  ? 'Loading events...'
                  : uploadMode === 'event' && eventJobsError
                    ? `Error: ${eventJobsError}`
                    : showError 
                      ? `Please select a ${uploadMode === 'event' ? 'event' : 'job description'}` 
                      : filteredTargets.length === 0
                        ? `No ${uploadMode === 'event' ? 'events' : 'job descriptions'} available`
                        : ''
              }
            >
              <MenuItem value="">
                <Typography color="text.secondary">
                  {uploadMode === 'event' && eventJobsLoading ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Loading events...
                    </>
                  ) : (
                    `Select a ${uploadMode === 'event' ? 'Event' : 'Job'}`
                  )}
                </Typography>
              </MenuItem>
              {!eventJobsLoading && filteredTargets.map(target => (
                <MenuItem key={target.id || target.jobId} value={target.id || target.jobId}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {target.id || target.jobId} - {target.title || target.jobTitle}
                    </Typography>
                    {uploadMode === 'event' && (
                      <Chip
                        label="Event"
                        size="small"
                        sx={{
                          ml: 1,
                          height: 16,
                          fontSize: '0.65rem',
                          backgroundColor: '#e0f2fe',
                          color: '#0288d1',
                        }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* ✅ Conditional Fields Based on Upload Mode */}
          {uploadMode === 'jd' ? (
            // JD Mode: Job Summary
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Job Summary (Optional)"
                value={jobSummary}
                onChange={e => setJobSummary(e.target.value)}
                placeholder="Brief summary of the job requirements..."
                helperText="Provide a concise overview of the role and key requirements"
              />
            </Grid>
          ) : (
            // Event Mode: Offering, Level, Skills
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Offering *"
                  value={offering}
                  onChange={e => setOffering(e.target.value)}
                  placeholder="e.g., Software Engineering, Data Science"
                  required
                  helperText="The domain or area of expertise for this event"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Level *"
                  value={level}
                  onChange={e => setLevel(e.target.value)}
                  required
                  helperText="Experience level for participants"
                >
                  <MenuItem value="">Select Level</MenuItem>
                  <MenuItem value="entry">Entry Level (0-2 years)</MenuItem>
                  <MenuItem value="mid">Mid Level (2-5 years)</MenuItem>
                  <MenuItem value="senior">Senior Level (5+ years)</MenuItem>
                  <MenuItem value="lead">Lead/Principal (8+ years)</MenuItem>
                  <MenuItem value="executive">Executive Level</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Skills *"
                  value={skills}
                  onChange={e => setSkills(e.target.value)}
                  placeholder="e.g., JavaScript, React, Node.js, Python, Machine Learning"
                  required
                  helperText="Key skills and technologies relevant to this event"
                />
              </Grid>
            </>
          )}


          <Grid item xs={12}>
            <Box
              sx={{
                border: `2px dashed ${dragOver ? 'var(--primary-blue)' : '#ccc'}`,
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: dragOver ? COLORS.GRAY_100 : 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'var(--primary-blue)',
                  backgroundColor: '#f9f9f9',
                },
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="resume-upload"
                key={`${selectedJobId}-${files.length}`}
              />
              <label htmlFor="resume-upload" style={{ cursor: 'pointer', display: 'block' }}>
                <UploadIcon
                  sx={{ fontSize: 48, color: dragOver ? 'var(--primary-blue)' : '#666', mb: 1 }}
                />
                <Typography
                  variant="h6"
                  sx={{ color: dragOver ? 'var(--primary-blue)' : 'inherit' }}
                >
                  {dragOver ? 'Drop files here' : 'Click to select resume files or drag & drop'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supports PDF, DOC, DOCX files (Max 10MB each)
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block' }}
                >
                  💡 Tip: Name files as "FirstName_LastName.pdf" for automatic candidate detection
                </Typography>
              </label>
            </Box>

            {files.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2">Selected Files ({files.length})</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total: {formatFileSize(getTotalSize())}
                  </Typography>
                </Box>

                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  {files.map((file, index) => (
                    <Box
                      key={`${file.name}-${index}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        mb: 1,
                        backgroundColor: '#fafafa',
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(file.size)} • {file.type.split('/')[1].toUpperCase()}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => removeFile(index)} sx={{ ml: 1 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectedJobId || files.length === 0 || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
          sx={{
            backgroundColor: 'var(--primary-blue)',
            '&:hover': {
              backgroundColor: 'var(--primary-blue)',
              filter: 'brightness(0.9)',
            },
          }}
        >
          {loading
            ? 'Processing...'
            : `Process ${files.length} Resume${files.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadResumeDialog;
